import { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Button, Card, Col, FloatingLabel, Form, Row, Spinner } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import LoggedInLayout from "../layouts/loggedin-layout";
import InsetAlert from "../components/insetalert";
import DotLoader from "../components/dotloader";
import PasskeyRecoveryCodes from "../components/passkey-recovery-codes";
import useUserStore from "../stores/user-store";
import {
    useChangeCurrentPasswordMutation,
    useCurrentAccountQuery,
    useDeletePasskeyMutation,
    useEnrollPasskeyMutation,
    useLogoutAllSessionsMutation,
    usePasskeySecuritySummaryQuery,
    useRegenerateRecoveryCodesMutation,
    useUpdateCurrentAccountMutation
} from "../new/auth.query";
import securityStyles from "../styles/passkey-security.module.css";
import { buildPasswordSchema, getPasswordRuleStates } from "./password-policy";

const defaultFriendlyName = "This device";

const profileValidationSchema = Yup.object({
    Username: Yup.string().trim().required("Required"),
    FirstName: Yup.string().trim().required("Required"),
    LastName: Yup.string().trim().required("Required")
});
const changePasswordValidationSchema = buildPasswordSchema({
    passwordField: "NewPassword",
    confirmPasswordField: "ConfirmNewPassword"
});

const normalizeProfileValue = (value) => (typeof value === "string" ? value.trim() : "");

const parseUtcDate = (value) => {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value !== "string") {
        const parsedValue = new Date(value);
        return Number.isNaN(parsedValue.getTime()) ? null : parsedValue;
    }

    const normalizedValue = value.trim();

    if (!normalizedValue) {
        return null;
    }

    const hasExplicitTimeZone = /([zZ]|[+-]\d{2}:\d{2})$/.test(normalizedValue);
    const parsedValue = new Date(hasExplicitTimeZone ? normalizedValue : `${normalizedValue}Z`);

    return Number.isNaN(parsedValue.getTime()) ? null : parsedValue;
};

const formatDateTime = (value, formatter) => {
    const parsedValue = parseUtcDate(value);

    if (!parsedValue) {
        return "Not used yet";
    }

    return formatter.format(parsedValue);
};

const resolveAuthenticationMethodLabel = (authenticationMethod) => {
    switch (authenticationMethod) {
        case "passkey":
            return "Passkey";
        case "recoveryCode":
            return "Recovery code";
        case "password":
        case "passwordEmailOtp":
            return "Password + emailed code";
        case "passwordSetup":
            return "Password setup";
        default:
            return "This session";
    }
};

const getPasswordExpirySummary = (account, formatter) => {
    if (!account?.passwordChangeAvailable) {
        return "Managed by passkeys";
    }

    if (account.passwordExpired) {
        return "Expired";
    }

    if (typeof account.passwordExpiresInDays === "number") {
        if (account.passwordExpiresInDays === 0) {
            return "Expires today";
        }

        if (account.passwordExpiresInDays === 1) {
            return "Expires in 1 day";
        }

        return `Expires in ${account.passwordExpiresInDays} days`;
    }

    return account.passwordExpiresAt
        ? formatDateTime(account.passwordExpiresAt, formatter)
        : "Unavailable";
};

const getPasswordExpiryDetail = (account, formatter) => {
    if (!account?.passwordChangeAvailable) {
        return "This account already uses passkeys for normal sign-in, so password rotation is no longer part of the normal sign-in flow.";
    }

    if (!account.passwordExpiresAt) {
        return "Change your password here if you want to rotate it before you enroll a passkey.";
    }

    if (account.passwordExpired) {
        return "This password has already expired. Reset it before you use password sign-in again.";
    }

    return `This password expires on ${formatDateTime(account.passwordExpiresAt, formatter)}. Changing it here signs out every other device while keeping this one active.`;
};

function PasskeySecurity() {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const onboarding = searchParams.get("onboarding") === "1";
    const returnTo = searchParams.get("returnTo") || "/";
    const { passkeyConfigured, authenticationMethod, updateProfile } = useUserStore((state) => ({
        passkeyConfigured: state.passkeyConfigured,
        authenticationMethod: state.authenticationMethod,
        updateProfile: state.updateProfile
    }));
    const [friendlyName, setFriendlyName] = useState(defaultFriendlyName);
    const [visibleRecoveryCodes, setVisibleRecoveryCodes] = useState([]);
    const [profileSavedMessage, setProfileSavedMessage] = useState("");
    const [passwordChangedMessage, setPasswordChangedMessage] = useState("");
    const localTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
    const dateTimeFormatter = useMemo(() => new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short"
    }), []);

    const currentAccountQuery = useCurrentAccountQuery(true);
    const summaryQuery = usePasskeySecuritySummaryQuery(true);
    const enrollMutation = useEnrollPasskeyMutation((session) => {
        setFriendlyName(defaultFriendlyName);

        if (session?.recoveryCodes?.length) {
            setVisibleRecoveryCodes(session.recoveryCodes);
        }
    });
    const updateCurrentAccountMutation = useUpdateCurrentAccountMutation((profile) => {
        updateProfile(profile);
        setProfileSavedMessage("Your account details were saved.");
    });
    const changeCurrentPasswordMutation = useChangeCurrentPasswordMutation(() => {
        setPasswordChangedMessage("Your password was updated and every other signed-in device was signed out.");
        summaryQuery.refetch();
    });
    const deletePasskeyMutation = useDeletePasskeyMutation();
    const regenerateRecoveryCodesMutation = useRegenerateRecoveryCodesMutation((codes) => {
        setVisibleRecoveryCodes(codes ?? []);
    });
    const logoutAllSessionsMutation = useLogoutAllSessionsMutation(() => {
        navigate("/Auth/Login", { replace: true });
    });

    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors, isDirty }
    } = useForm({
        defaultValues: {
            Username: "",
            FirstName: "",
            LastName: ""
        },
        resolver: yupResolver(profileValidationSchema)
    });
    const [watchedUsername, watchedFirstName, watchedLastName] = watch(["Username", "FirstName", "LastName"]);
    const {
        register: registerPassword,
        handleSubmit: handlePasswordSubmit,
        reset: resetPasswordForm,
        watch: watchPasswordForm,
        formState: {
            errors: passwordErrors,
            isDirty: isPasswordDirty
        }
    } = useForm({
        defaultValues: {
            CurrentPassword: "",
            NewPassword: "",
            ConfirmNewPassword: ""
        },
        resolver: yupResolver(changePasswordValidationSchema)
    });
    const [newPasswordValue, confirmNewPasswordValue] = watchPasswordForm(["NewPassword", "ConfirmNewPassword"]);

    useEffect(() => {
        if (onboarding && !passkeyConfigured) {
            document.title = "MFP - Finish Account Setup";
            return;
        }

        document.title = "MFP - Account Settings";
    }, [onboarding, passkeyConfigured]);

    useEffect(() => {
        if (!currentAccountQuery.data) {
            return;
        }

        reset({
            Username: currentAccountQuery.data.username ?? "",
            FirstName: currentAccountQuery.data.firstName ?? "",
            LastName: currentAccountQuery.data.lastName ?? ""
        });
        updateProfile(currentAccountQuery.data);
    }, [currentAccountQuery.data, reset, updateProfile]);

    useEffect(() => {
        if (isDirty && profileSavedMessage) {
            setProfileSavedMessage("");
        }
    }, [isDirty, profileSavedMessage]);

    useEffect(() => {
        if (isPasswordDirty && passwordChangedMessage) {
            setPasswordChangedMessage("");
        }
    }, [isPasswordDirty, passwordChangedMessage]);

    const passkeys = summaryQuery.data?.passkeys ?? [];
    const sessions = summaryQuery.data?.sessions ?? [];
    const recoveryCodesRemaining = summaryQuery.data?.recoveryCodesRemaining ?? 0;
    const accountUsesPasswordSignIn = currentAccountQuery.data?.passwordChangeAvailable ?? false;
    const accountEmail = currentAccountQuery.data?.email ?? "";
    const accountName = `${currentAccountQuery.data?.firstName ?? ""} ${currentAccountQuery.data?.lastName ?? ""}`.trim();
    const profileBusy = updateCurrentAccountMutation.isPending;
    const passwordBusy = changeCurrentPasswordMutation.isPending;
    const passwordRuleStates = getPasswordRuleStates(newPasswordValue ?? "", confirmNewPasswordValue ?? "");
    const passwordExpirySummary = useMemo(
        () => getPasswordExpirySummary(currentAccountQuery.data, dateTimeFormatter),
        [currentAccountQuery.data, dateTimeFormatter]
    );
    const passwordExpiryDetail = useMemo(
        () => getPasswordExpiryDetail(currentAccountQuery.data, dateTimeFormatter),
        [currentAccountQuery.data, dateTimeFormatter]
    );
    const profileHasChanges = useMemo(() => {
        if (!currentAccountQuery.data) {
            return false;
        }

        return normalizeProfileValue(watchedUsername) !== normalizeProfileValue(currentAccountQuery.data.username) ||
            normalizeProfileValue(watchedFirstName) !== normalizeProfileValue(currentAccountQuery.data.firstName) ||
            normalizeProfileValue(watchedLastName) !== normalizeProfileValue(currentAccountQuery.data.lastName);
    }, [
        currentAccountQuery.data,
        watchedFirstName,
        watchedLastName,
        watchedUsername
    ]);
    const securityBusy =
        enrollMutation.isPending ||
        deletePasskeyMutation.isPending ||
        regenerateRecoveryCodesMutation.isPending ||
        logoutAllSessionsMutation.isPending;
    const currentMethodLabel = resolveAuthenticationMethodLabel(authenticationMethod);
    const normalSignInLabel = passkeyConfigured ? "Passkey only" : "Password plus emailed login code";
    const securityStatus = passkeyConfigured ? {
        toneClassName: securityStyles.securityStatusStrong,
        eyebrow: "Strongest protection enabled",
        title: "Passkey protection is active on this account",
        copy: "Your account now uses passkeys for normal sign-in. Keep at least two trusted passkeys and store your recovery codes somewhere outside this app."
    } : {
        toneClassName: securityStyles.securityStatusUpgrade,
        eyebrow: "Stronger protection available",
        title: "This account is not yet using the strongest sign-in protection",
        copy: onboarding
            ? "Your password is ready and you can keep using password plus emailed login code for now, but adding a passkey here gives this account the strongest protection."
            : "This account still relies on password plus emailed login code. Add your first passkey to move to the strongest sign-in protection available in MFP."
    };

    const handleProfileSubmit = async (values) => {
        if (!profileHasChanges) {
            return;
        }

        try {
            await updateCurrentAccountMutation.mutateAsync({
                username: normalizeProfileValue(values.Username),
                firstName: normalizeProfileValue(values.FirstName),
                lastName: normalizeProfileValue(values.LastName)
            });
        } catch {
            // Expected save failures are surfaced through the mutation state and section alert.
        }
    };

    const handleEnrollmentSubmit = async (event) => {
        event.preventDefault();

        try {
            await enrollMutation.mutateAsync({
                friendlyName: friendlyName?.trim() || defaultFriendlyName
            });
        } catch {
            // Expected enrollment failures are displayed by the mutation error state.
        }
    };

    const handlePasswordChangeSubmit = async (values) => {
        try {
            await changeCurrentPasswordMutation.mutateAsync({
                currentPassword: values.CurrentPassword,
                newPassword: values.NewPassword,
                confirmPassword: values.ConfirmNewPassword
            });
            resetPasswordForm({
                CurrentPassword: "",
                NewPassword: "",
                ConfirmNewPassword: ""
            });
        } catch {
            // Expected password-change failures are displayed by the mutation error state.
        }
    };

    const handleDeletePasskey = async (credentialId, displayName) => {
        const confirmed = window.confirm(`Remove the passkey named "${displayName}"?`);

        if (!confirmed) {
            return;
        }

        try {
            await deletePasskeyMutation.mutateAsync(credentialId);
        } catch {
            // Expected delete failures are displayed by the mutation error state.
        }
    };

    const handleRegenerateRecoveryCodes = async () => {
        const confirmed = window.confirm("Generating new recovery codes will immediately invalidate your remaining unused recovery codes. Continue?");

        if (!confirmed) {
            return;
        }

        try {
            await regenerateRecoveryCodesMutation.mutateAsync();
        } catch {
            // Expected regeneration failures are displayed by the mutation error state.
        }
    };

    const handleLogoutAllDevices = async () => {
        const confirmed = window.confirm("This will immediately sign out every device currently using your account, including this one. Continue?");

        if (!confirmed) {
            return;
        }

        try {
            await logoutAllSessionsMutation.mutateAsync();
        } catch {
            // Expected logout-all failures are displayed by the mutation error state.
        }
    };

    return (
        <LoggedInLayout>
            <div className={securityStyles.shell}>
                <Card className={securityStyles.panel}>
                    <Card.Body className={securityStyles.panelBody}>
                        <p className={securityStyles.eyebrow}>Account Settings</p>
                        <h3 className={securityStyles.title}>
                            {onboarding && !passkeyConfigured ? "Finish setting up your account" : "Manage your profile and sign-in security"}
                        </h3>
                        <p className={securityStyles.subtitle}>
                            {onboarding && !passkeyConfigured
                                ? `Your password is set${accountName ? ` for ${accountName}` : ""}. You can add a passkey now or skip for now and come back later from Account Settings.`
                                : "Update your profile details, review your current sign-in setup, manage passkeys, and control which devices stay signed in."}
                        </p>

                        <div className={`${securityStyles.securityStatusCard} ${securityStatus.toneClassName}`}>
                            <p className={securityStyles.securityStatusEyebrow}>{securityStatus.eyebrow}</p>
                            <h5 className={securityStyles.securityStatusTitle}>{securityStatus.title}</h5>
                            <p className={securityStyles.securityStatusCopy}>{securityStatus.copy}</p>
                        </div>

                        <section className={securityStyles.sectionPanel}>
                            <div className={securityStyles.sectionHeaderRow}>
                                <div>
                                    <h5 className={securityStyles.sectionTitle}>Profile details</h5>
                                    <p className={securityStyles.sectionCopy}>
                                        Update the account name and username used throughout MFP. Your email is shown here because login codes and recovery notices are sent to it.
                                    </p>
                                </div>
                            </div>

                            {currentAccountQuery.error ? (
                                <InsetAlert error={{ error: currentAccountQuery.error.message }} heading="Unable To Load Account Details" style={{ width: "100%" }} />
                            ) : currentAccountQuery.isLoading ? (
                                <div className={securityStyles.loadingInline}>
                                    <Spinner animation="border" size="sm" />
                                    <span>Loading your account details...</span>
                                </div>
                            ) : (
                                <Form onSubmit={handleSubmit(handleProfileSubmit)}>
                                    {updateCurrentAccountMutation.error ? (
                                        <InsetAlert error={{ error: updateCurrentAccountMutation.error.message }} heading="Profile Update Failed" style={{ width: "100%" }} />
                                    ) : null}
                                    {profileSavedMessage ? (
                                        <div className={securityStyles.inlineSuccess} role="status" aria-live="polite">
                                            <span className={securityStyles.inlineSuccessIndicator} aria-hidden="true" />
                                            <span>{profileSavedMessage}</span>
                                        </div>
                                    ) : null}
                                    <div className={securityStyles.formGrid}>
                                        <FloatingLabel controlId="account-username" label="Username" className={securityStyles.formField}>
                                            <Form.Control
                                                type="text"
                                                placeholder="Username"
                                                autoComplete="username"
                                                isInvalid={!!errors.Username}
                                                disabled={profileBusy}
                                                {...register("Username")}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.Username?.message}
                                            </Form.Control.Feedback>
                                        </FloatingLabel>
                                        <FloatingLabel controlId="account-first-name" label="First Name" className={securityStyles.formField}>
                                            <Form.Control
                                                type="text"
                                                placeholder="First Name"
                                                autoComplete="given-name"
                                                isInvalid={!!errors.FirstName}
                                                disabled={profileBusy}
                                                {...register("FirstName")}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.FirstName?.message}
                                            </Form.Control.Feedback>
                                        </FloatingLabel>
                                        <FloatingLabel controlId="account-last-name" label="Last Name" className={securityStyles.formField}>
                                            <Form.Control
                                                type="text"
                                                placeholder="Last Name"
                                                autoComplete="family-name"
                                                isInvalid={!!errors.LastName}
                                                disabled={profileBusy}
                                                {...register("LastName")}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.LastName?.message}
                                            </Form.Control.Feedback>
                                        </FloatingLabel>
                                        <FloatingLabel controlId="account-email" label="Primary Email" className={`${securityStyles.formField} ${securityStyles.formFieldFull}`}>
                                            <Form.Control
                                                type="email"
                                                placeholder="Primary Email"
                                                value={accountEmail}
                                                readOnly
                                                disabled
                                            />
                                        </FloatingLabel>
                                    </div>
                                    <p className={securityStyles.fieldHint}>
                                        Email login codes and recovery notices are sent to this address. If this email needs to change, contact an administrator.
                                    </p>
                                    <div className={securityStyles.sectionActions}>
                                        <Button type="submit" disabled={profileBusy || currentAccountQuery.isLoading || !profileHasChanges}>
                                            {profileBusy ? <DotLoader ButtonStyle={true} /> : "Save Profile Changes"}
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </section>

                        <section className={securityStyles.sectionPanel}>
                            <div className={securityStyles.sectionHeaderRow}>
                                <div>
                                    <h5 className={securityStyles.sectionTitle}>Sign-in security</h5>
                                    <p className={securityStyles.sectionCopy}>
                                        This section reflects how your account signs in today and lets you register passkeys on the device you are using right now.
                                    </p>
                                </div>
                            </div>

                            {summaryQuery.error ? (
                                <InsetAlert error={{ error: summaryQuery.error.message }} heading="Unable To Load Security Settings" style={{ width: "100%" }} />
                            ) : null}
                            {enrollMutation.error ? (
                                <InsetAlert error={{ error: enrollMutation.error.message }} heading="Passkey Setup Failed" style={{ width: "100%" }} />
                            ) : null}

                            <div className={securityStyles.securitySetupGrid}>
                                <div className={securityStyles.securityColumn}>
                                    <Row className="g-3 mb-3">
                                        <Col sm={6} xl={4}>
                                            <div className={securityStyles.statCard}>
                                                <p className={securityStyles.statLabel}>Registered passkeys</p>
                                                <p className={securityStyles.statValue}>{passkeys.length}</p>
                                            </div>
                                        </Col>
                                        <Col sm={6} xl={4}>
                                            <div className={securityStyles.statCard}>
                                                <p className={securityStyles.statLabel}>Recovery codes remaining</p>
                                                <p className={securityStyles.statValue}>{recoveryCodesRemaining}</p>
                                            </div>
                                        </Col>
                                        <Col sm={6} xl={4}>
                                            <div className={securityStyles.statCard}>
                                                <p className={securityStyles.statLabel}>Signed-in devices</p>
                                                <p className={securityStyles.statValue}>{sessions.length}</p>
                                            </div>
                                        </Col>
                                    </Row>

                                    <div className={securityStyles.infoGrid}>
                                        <div className={securityStyles.infoCard}>
                                            <p className={securityStyles.infoLabel}>Normal sign-in</p>
                                            <p className={securityStyles.infoValue}>{normalSignInLabel}</p>
                                        </div>
                                        <div className={securityStyles.infoCard}>
                                            <p className={securityStyles.infoLabel}>Current session authenticated with</p>
                                            <p className={securityStyles.infoValue}>{currentMethodLabel}</p>
                                        </div>
                                        <div className={securityStyles.infoCard}>
                                            <p className={securityStyles.infoLabel}>Email for login codes</p>
                                            <p className={securityStyles.infoValue}>{accountEmail || "Unavailable"}</p>
                                        </div>
                                        <div className={securityStyles.infoCard}>
                                            <p className={securityStyles.infoLabel}>Password status</p>
                                            <p className={securityStyles.infoValue}>{passwordExpirySummary}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className={securityStyles.actionCard}>
                                    <h6 className={securityStyles.actionCardTitle}>
                                        {passkeyConfigured ? "Register another passkey" : "Register your first passkey"}
                                    </h6>
                                    <p className={securityStyles.actionCardCopy}>
                                        Use Face ID, fingerprint, Windows Hello, a security key, or your device PIN to create a phishing-resistant sign-in credential.
                                    </p>
                                    <Form onSubmit={handleEnrollmentSubmit}>
                                        <FloatingLabel controlId="passkey-friendly-name" label="Device name" className="mb-3">
                                            <Form.Control
                                                type="text"
                                                placeholder="Device name"
                                                value={friendlyName}
                                                disabled={securityBusy}
                                                onChange={(event) => setFriendlyName(event.target.value)}
                                            />
                                        </FloatingLabel>
                                        <div className={securityStyles.sectionActions}>
                                            <Button type="submit" disabled={securityBusy}>
                                                {enrollMutation.isPending ? <DotLoader ButtonStyle={true} /> : passkeyConfigured ? "Add Passkey" : "Create Passkey"}
                                            </Button>
                                            {onboarding ? (
                                                <Button
                                                    type="button"
                                                    variant="outline-secondary"
                                                    disabled={securityBusy}
                                                    onClick={() => navigate(returnTo)}
                                                >
                                                    {passkeyConfigured ? "Continue to application" : "Skip for now"}
                                                </Button>
                                            ) : (
                                                <Button
                                                    type="button"
                                                    variant="outline-secondary"
                                                    disabled={securityBusy}
                                                    onClick={() => navigate(returnTo)}
                                                >
                                                    Back to app
                                                </Button>
                                            )}
                                        </div>
                                    </Form>
                                    {!passkeyConfigured ? (
                                        <p className={securityStyles.actionCardNote}>
                                            Adding your first passkey makes this account passkey-only for normal sign-in while keeping recovery codes available for emergencies.
                                        </p>
                                    ) : (
                                        <p className={securityStyles.actionCardNote}>
                                            Keep at least two passkeys when possible so a lost device does not lock you out.
                                        </p>
                                    )}
                                </div>
                            </div>

                            <PasskeyRecoveryCodes codes={visibleRecoveryCodes} />
                            {visibleRecoveryCodes.length ? (
                                <p className={securityStyles.sectionNote}>
                                    These recovery codes are only shown once. Store them somewhere secure before leaving this page.
                                </p>
                            ) : null}
                        </section>

                        <section className={securityStyles.sectionPanel}>
                            <div className={securityStyles.sectionHeaderRow}>
                                <div>
                                    <h5 className={securityStyles.sectionTitle}>Password sign-in</h5>
                                    <p className={securityStyles.sectionCopy}>
                                        {passwordExpiryDetail}
                                    </p>
                                </div>
                            </div>

                            {currentAccountQuery.isLoading ? (
                                <div className={securityStyles.loadingInline}>
                                    <Spinner animation="border" size="sm" />
                                    <span>Loading password settings...</span>
                                </div>
                            ) : !accountUsesPasswordSignIn ? (
                                <div className={securityStyles.passwordStatusPanel}>
                                    <p className={securityStyles.passwordStatusTitle}>Password sign-in is no longer the normal path for this account.</p>
                                    <p className={securityStyles.passwordStatusCopy}>
                                        Once a passkey is enrolled, normal sign-in becomes passkey-only. Recovery codes remain available for emergencies.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {changeCurrentPasswordMutation.error ? (
                                        <InsetAlert error={{ error: changeCurrentPasswordMutation.error.message }} heading="Password Change Failed" style={{ width: "100%" }} />
                                    ) : null}
                                    {passwordChangedMessage ? (
                                        <div className={securityStyles.inlineSuccess} role="status" aria-live="polite">
                                            <span className={securityStyles.inlineSuccessIndicator} aria-hidden="true" />
                                            <span>{passwordChangedMessage}</span>
                                        </div>
                                    ) : null}
                                    <div className={securityStyles.passwordStatusPanel}>
                                        <p className={securityStyles.passwordStatusTitle}>{passwordExpirySummary}</p>
                                        <p className={securityStyles.passwordStatusCopy}>
                                            {passwordExpiryDetail}
                                        </p>
                                    </div>
                                    <Form onSubmit={handlePasswordSubmit(handlePasswordChangeSubmit)}>
                                        <div className={securityStyles.passwordRulesPanel}>
                                            <p className={securityStyles.passwordRulesTitle}>Before you submit, make sure your password:</p>
                                            <ul className={securityStyles.passwordRulesList}>
                                                {passwordRuleStates.map((rule) => (
                                                    <li
                                                        key={rule.id}
                                                        className={`${securityStyles.passwordRuleItem} ${rule.met ? securityStyles.passwordRuleItemMet : ""}`}
                                                    >
                                                        <span
                                                            className={`${securityStyles.passwordRuleIndicator} ${rule.met ? securityStyles.passwordRuleIndicatorMet : ""}`}
                                                            aria-hidden="true"
                                                        />
                                                        <span>{rule.label}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className={securityStyles.passwordFieldStack}>
                                            <FloatingLabel controlId="account-current-password" label="Current Password" className={`${securityStyles.formField} ${securityStyles.passwordFieldBreak}`}>
                                                <Form.Control
                                                    type="password"
                                                    placeholder="Current Password"
                                                    autoComplete="current-password"
                                                    isInvalid={!!passwordErrors.CurrentPassword}
                                                    disabled={passwordBusy}
                                                    {...registerPassword("CurrentPassword")}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {passwordErrors.CurrentPassword?.message}
                                                </Form.Control.Feedback>
                                            </FloatingLabel>
                                            <FloatingLabel controlId="account-new-password" label="New Password" className={securityStyles.formField}>
                                                <Form.Control
                                                    type="password"
                                                    placeholder="New Password"
                                                    autoComplete="new-password"
                                                    isInvalid={!!passwordErrors.NewPassword}
                                                    disabled={passwordBusy}
                                                    {...registerPassword("NewPassword")}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {passwordErrors.NewPassword?.message}
                                                </Form.Control.Feedback>
                                            </FloatingLabel>
                                            <FloatingLabel controlId="account-confirm-password" label="Confirm New Password" className={securityStyles.formField}>
                                                <Form.Control
                                                    type="password"
                                                    placeholder="Confirm New Password"
                                                    autoComplete="new-password"
                                                    isInvalid={!!passwordErrors.ConfirmNewPassword}
                                                    disabled={passwordBusy}
                                                    {...registerPassword("ConfirmNewPassword")}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {passwordErrors.ConfirmNewPassword?.message}
                                                </Form.Control.Feedback>
                                            </FloatingLabel>
                                        </div>
                                        <div className={securityStyles.sectionActions}>
                                            <Button type="submit" disabled={passwordBusy || !isPasswordDirty}>
                                                {passwordBusy ? <DotLoader ButtonStyle={true} /> : "Change Password"}
                                            </Button>
                                        </div>
                                    </Form>
                                </>
                            )}
                        </section>

                        <section className={securityStyles.sectionPanel}>
                            <div className={securityStyles.sectionHeaderRow}>
                                <div>
                                    <h5 className={securityStyles.sectionTitle}>Registered passkeys</h5>
                                    <p className={securityStyles.sectionCopy}>
                                        Review the trusted devices and security keys attached to this account. Times are shown in your local time{localTimeZone ? ` (${localTimeZone})` : ""}.
                                    </p>
                                </div>
                                <Button
                                    type="button"
                                    variant="outline-secondary"
                                    disabled={securityBusy || summaryQuery.isLoading}
                                    onClick={() => summaryQuery.refetch()}
                                >
                                    Refresh
                                </Button>
                            </div>
                            {summaryQuery.isLoading ? (
                                <div className={securityStyles.loadingInline}>
                                    <Spinner animation="border" size="sm" />
                                    <span>Loading passkey information...</span>
                                </div>
                            ) : passkeys.length ? (
                                <div className={securityStyles.passkeyList}>
                                    {passkeys.map((passkey) => (
                                        <div key={passkey.id} className={securityStyles.passkeyCard}>
                                            <div className={securityStyles.itemHeaderRow}>
                                                <div>
                                                    <h6 className="mb-1">{passkey.friendlyName}</h6>
                                                    <p className={securityStyles.passkeyMeta}>
                                                        Added {formatDateTime(passkey.createdAt, dateTimeFormatter)}
                                                    </p>
                                                    <p className={securityStyles.passkeyMeta}>
                                                        Last used {formatDateTime(passkey.lastUsedAt, dateTimeFormatter)}
                                                    </p>
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant="outline-danger"
                                                    disabled={securityBusy || passkeys.length <= 1}
                                                    onClick={() => handleDeletePasskey(passkey.id, passkey.friendlyName)}
                                                >
                                                    Remove
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className={securityStyles.emptyState}>No passkeys are registered yet.</p>
                            )}
                            {deletePasskeyMutation.error ? (
                                <InsetAlert error={{ error: deletePasskeyMutation.error.message }} heading="Passkey Removal Failed" style={{ width: "100%" }} />
                            ) : null}
                            {passkeys.length <= 1 ? (
                                <p className={securityStyles.sectionNote}>
                                    The last remaining passkey cannot be removed. Add another trusted device first.
                                </p>
                            ) : null}
                        </section>

                        <section className={securityStyles.sectionPanel}>
                            <div className={securityStyles.sectionHeaderRow}>
                                <div>
                                    <h5 className={securityStyles.sectionTitle}>Signed-in devices</h5>
                                    <p className={securityStyles.sectionCopy}>
                                        Review every active browser or device session tied to your account. Session times are shown in your local time{localTimeZone ? ` (${localTimeZone})` : ""}.
                                    </p>
                                </div>
                                <div className={securityStyles.sectionActions}>
                                    <Button
                                        type="button"
                                        variant="outline-secondary"
                                        disabled={securityBusy || summaryQuery.isLoading}
                                        onClick={() => summaryQuery.refetch()}
                                    >
                                        Refresh
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline-danger"
                                        disabled={securityBusy || !sessions.length}
                                        onClick={handleLogoutAllDevices}
                                    >
                                        {logoutAllSessionsMutation.isPending ? <DotLoader ButtonStyle={true} /> : "Sign Out All Devices"}
                                    </Button>
                                </div>
                            </div>
                            {logoutAllSessionsMutation.error ? (
                                <InsetAlert error={{ error: logoutAllSessionsMutation.error.message }} heading="Device Sign-out Failed" style={{ width: "100%" }} />
                            ) : null}
                            {summaryQuery.isLoading ? (
                                <div className={securityStyles.loadingInline}>
                                    <Spinner animation="border" size="sm" />
                                    <span>Loading session information...</span>
                                </div>
                            ) : sessions.length ? (
                                <div className={securityStyles.sessionList}>
                                    {sessions.map((session) => {
                                        const showInitialIp = session.initialIpAddress && session.initialIpAddress !== session.lastIpAddress;

                                        return (
                                            <div key={session.id} className={securityStyles.sessionCard}>
                                                <div className={securityStyles.sessionBody}>
                                                    <div className={securityStyles.itemHeaderRow}>
                                                        <div className={securityStyles.itemHeaderWrap}>
                                                            <h6 className="mb-0">{session.deviceName || "Unknown device"}</h6>
                                                            {session.isCurrent ? (
                                                                <span className={securityStyles.sessionBadge}>Current device</span>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                    <p className={securityStyles.sessionMeta}>
                                                        Signed in {formatDateTime(session.createdAt, dateTimeFormatter)}
                                                    </p>
                                                    <p className={securityStyles.sessionMeta}>
                                                        Last active {formatDateTime(session.lastUsedAt, dateTimeFormatter)}
                                                    </p>
                                                    <p className={securityStyles.sessionMeta}>
                                                        Latest IP {session.lastIpAddress || session.initialIpAddress || "Unavailable"}
                                                    </p>
                                                    {showInitialIp ? (
                                                        <p className={securityStyles.sessionMeta}>
                                                            First sign-in IP {session.initialIpAddress}
                                                        </p>
                                                    ) : null}
                                                    {session.userAgent ? (
                                                        <p className={securityStyles.sessionUserAgent}>
                                                            {session.userAgent}
                                                        </p>
                                                    ) : null}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className={securityStyles.emptyState}>No active sessions were found for this account.</p>
                            )}
                        </section>

                        <section className={securityStyles.sectionPanel}>
                            <div className={securityStyles.sectionHeaderRow}>
                                <div>
                                    <h5 className={securityStyles.sectionTitle}>Recovery codes</h5>
                                    <p className={securityStyles.sectionCopy}>
                                        Use a recovery code only when you cannot reach any registered passkey. Creating a new set immediately invalidates any unused old codes.
                                    </p>
                                </div>
                            </div>
                            {regenerateRecoveryCodesMutation.error ? (
                                <InsetAlert error={{ error: regenerateRecoveryCodesMutation.error.message }} heading="Recovery Codes Failed" style={{ width: "100%" }} />
                            ) : null}
                            <div className={securityStyles.sectionActions}>
                                <Button
                                    type="button"
                                    variant="outline-primary"
                                    disabled={securityBusy || !passkeys.length}
                                    onClick={handleRegenerateRecoveryCodes}
                                >
                                    {regenerateRecoveryCodesMutation.isPending ? <DotLoader ButtonStyle={true} /> : "Generate New Recovery Codes"}
                                </Button>
                                <span className={securityStyles.sectionMeta}>
                                    {recoveryCodesRemaining} unused code{recoveryCodesRemaining === 1 ? "" : "s"} remaining
                                </span>
                            </div>
                        </section>
                    </Card.Body>
                </Card>
            </div>
        </LoggedInLayout>
    );
}

export default PasskeySecurity;
